import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import WelcomeHeader from './components/WelcomeHeader';
import CourseCard from './components/CourseCard';
import MentorCard from './components/MentorCard';
import ProjectShowcase from './components/ProjectShowcase';
import CompetitionArena from './components/CompetitionArena';
import PortfolioBuilder from './components/PortfolioBuilder';
import ProgressTracker from './components/ProgressTracker';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const IndividualLearningPortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [courseFilter, setCourseFilter] = useState('all');

  // Mock user data
  const userData = {
    name: "Neel Mehta",
    title: "Python Developer",
    level: 9,
    streak: 12,
    achievements: 23,
    avatar: "../public/assets/images/logo.JPG",
    tracks: [
      {
        name: "AI/ML",
        icon: "Brain",
        color: "text-accent",
        progress: 49,
        gradient: "from-accent to-accent/80",
        nextMilestone: "Complete Neural Networks course"
      },
      {
        name: "Robotics",
        icon: "Bot",
        color: "text-cyber-purple",
        progress: 95,
        gradient: "from-cyber-purple to-cyber-purple/80",
        nextMilestone: "Build autonomous robot project"
      },
      {
        name: "Coding",
        icon: "Code",
        color: "text-success",
        progress: 90,
        gradient: "from-success to-success/80",
        nextMilestone: "Master advanced algorithms"
      }
    ]
  };

  const userStats = [
    { icon: "BookOpen", value: "12", label: "Courses", bgColor: "bg-accent" },
    { icon: "Trophy", value: "8", label: "Completed", bgColor: "bg-success" },
    { icon: "Clock", value: "156h", label: "Learning", bgColor: "bg-warning" }
  ];

  // Mock courses data
  const coursesData = [
    {
      id: 1,
      title: "Advanced Machine Learning with Python",
      description: "Master deep learning, neural networks, and AI algorithms with hands-on projects and real-world applications.",
      thumbnail: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
      difficulty: "Advanced",
      duration: "12 weeks",
      rating: 4.8,
      enrolled: "2.3k",
      completionRate: 87,
      jobPlacement: 92,
      progress: 0,
      technologies: ["Python", "TensorFlow", "PyTorch", "Scikit-learn"],
      instructor: {
        name: "Dr. Sarah Kim",
        company: "Google AI",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b9c7b6b5?w=50&h=50&fit=crop&crop=face"
      }
    },
    {
      id: 2,
      title: "Robotics Fundamentals & Arduino Programming",
      description: "Build and program robots from scratch using Arduino, sensors, and actuators in this comprehensive course.",
      thumbnail: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=250&fit=crop",
      difficulty: "Beginner",
      duration: "8 weeks",
      rating: 4.9,
      enrolled: "1.8k",
      completionRate: 94,
      jobPlacement: 78,
      progress: 35,
      technologies: ["Arduino", "C++", "Electronics", "3D Printing"],
      instructor: {
        name: "Mark Rodriguez",
        company: "Boston Dynamics",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face"
      }
    },
    {
      id: 3,
      title: "Full Stack Web Development Bootcamp",
      description: "Complete web development course covering React, Node.js, databases, and deployment strategies.",
      thumbnail: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop",
      difficulty: "Intermediate",
      duration: "16 weeks",
      rating: 4.7,
      enrolled: "3.1k",
      completionRate: 89,
      jobPlacement: 95,
      progress: 68,
      technologies: ["React", "Node.js", "MongoDB", "AWS"],
      instructor: {
        name: "Emily Johnson",
        company: "Meta",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face"
      }
    }
  ];

  // Mock mentors data
  const mentorsData = [
    {
      id: 1,
      name: "Chirag Sharma",
      title: "Data Science Explorer",
      company: "Dream Coders Club",
      avatar: "",
      rating: 4.9,
      experience: "10+",
      mentees: 45,
      sessions: 120,
      status: "Available",
      expertise: ["Deep Learning", "Computer Vision", "Data Science", "Python"],
      bio: "Pursued Data Science Masters and now teaching and mentoring many students to competitions.",
      nextAvailable: "Today 3:00 PM"
    },
    // {
    //   id: 2,
    //   name: "Lisa Chen",
    //   title: "Robotics Engineering Manager",
    //   company: "Boston Dynamics",
    //   avatar: "https://images.unsplash.com/photo-1494790108755-2616b9c7b6b5?w=100&h=100&fit=crop&crop=face",
    //   rating: 4.8,
    //   experience: 12,
    //   mentees: 38,
    //   sessions: 95,
    //   status: "Busy",
    //   expertise: ["Robotics", "Control Systems", "Mechanical Design", "ROS"],
    //   bio: "Engineering manager at Boston Dynamics working on humanoid robots. Expert in control systems and mechanical design with 12+ years experience.",
    //   nextAvailable: "Tomorrow 10:00 AM"
    // }
  ];

  // Mock projects data
  const projectsData = [
    {
      id: 1,
      title: "AI-Powered Code Review Assistant",
      description: "An intelligent system that automatically reviews code, suggests improvements, and detects potential bugs using machine learning.",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
      category: "AI/ML",
      featured: true,
      creator: {
        name: "Alex Chen",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face"
      },
      timeAgo: "2 days ago",
      technologies: ["Python", "TensorFlow", "React", "Node.js"],
      likes: 234,
      comments: 45,
      views: 1200
    },
    {
      id: 2,
      title: "Autonomous Delivery Drone",
      description: "A fully autonomous drone capable of package delivery with obstacle avoidance and GPS navigation.",
      image: "https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=400&h=250&fit=crop",
      category: "Robotics",
      featured: false,
      creator: {
        name: "Sarah Kim",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b9c7b6b5?w=50&h=50&fit=crop&crop=face"
      },
      timeAgo: "1 week ago",
      technologies: ["Arduino", "C++", "Computer Vision", "GPS"],
      likes: 189,
      comments: 32,
      views: 890
    }
  ];

  // Mock competitions data
  const competitionsData = [
    {
      id: 1,
      title: "AI Hackathon 2024: Healthcare Innovation",
      description: "Build AI solutions to solve real-world healthcare challenges. Focus on diagnosis, treatment optimization, and patient care.",
      status: "Active",
      endDate: "Dec 15, 2024",
      participants: 1247,
      difficulty: "Advanced",
      prize: "$25,000",
      timeRemaining: "5 days, 14 hours",
      progress: 65,
      technologies: ["Python", "TensorFlow", "Healthcare APIs", "Data Science"],
      joined: false,
      topParticipants: [
        { name: "John Doe", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face" },
        { name: "Jane Smith", avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face" }
      ]
    },
    {
      id: 2,
      title: "Robotics Challenge: Autonomous Navigation",
      description: "Design and program robots to navigate complex environments autonomously using sensors and AI algorithms.",
      status: "Upcoming",
      endDate: "Jan 20, 2025",
      participants: 856,
      difficulty: "Intermediate",
      prize: "$15,000",
      timeRemaining: "Registration opens in 3 days",
      progress: 0,
      technologies: ["ROS", "Python", "Computer Vision", "Arduino"],
      joined: true,
      topParticipants: [
        { name: "Mike Johnson", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face" }
      ]
    }
  ];

  // Mock leaderboard data
  const leaderboardData = [
    {
      id: 1,
      name: "Alex Chen",
      title: "AI Specialist",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face",
      points: 2450
    },
    {
      id: 2,
      name: "Sarah Kim",
      title: "Robotics Engineer",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b9c7b6b5?w=50&h=50&fit=crop&crop=face",
      points: 2380
    },
    {
      id: 3,
      name: "Mike Rodriguez",
      title: "Full Stack Dev",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
      points: 2290
    }
  ];

  // Mock portfolio data
  const portfolioData = {
    projects: [
      {
        id: 1,
        title: "Smart Home Automation System",
        description: "IoT-based home automation with voice control and mobile app integration.",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop",
        technologies: ["React Native", "Node.js", "Arduino", "AWS IoT"],
        stars: 45,
        views: 1200
      },
      {
        id: 2,
        title: "Machine Learning Stock Predictor",
        description: "AI model for predicting stock prices using historical data and market indicators.",
        image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop",
        technologies: ["Python", "TensorFlow", "Pandas", "Flask"],
        stars: 67,
        views: 890
      }
    ],
    skills: [
      {
        name: "Python",
        category: "Programming",
        level: "Expert",
        percentage: 95,
        experience: "4 years",
        projects: 12,
        icon: "Code"
      },
      {
        name: "Machine Learning",
        category: "AI/ML",
        level: "Advanced",
        percentage: 85,
        experience: "2 years",
        projects: 8,
        icon: "Brain"
      },
      {
        name: "React",
        category: "Frontend",
        level: "Advanced",
        percentage: 80,
        experience: "3 years",
        projects: 15,
        icon: "Globe"
      }
    ],
    achievements: [
      {
        title: "AI Hackathon Winner",
        description: "1st place in Healthcare AI Challenge 2024",
        date: "Nov 2024",
        icon: "Trophy",
        bgColor: "bg-warning"
      },
      {
        title: "Google Cloud Certified",
        description: "Professional Machine Learning Engineer",
        date: "Oct 2024",
        icon: "Award",
        bgColor: "bg-accent"
      }
    ],
    feedback: [
      {
        type: "strength",
        title: "Strong Technical Foundation",
        description: "Your portfolio demonstrates excellent technical skills across multiple domains including AI, web development, and robotics."
      },
      {
        type: "improvement",
        title: "Add More Project Documentation",
        description: "Consider adding detailed README files and documentation to showcase your problem-solving approach."
      },
      {
        type: "suggestion",
        title: "Highlight Leadership Experience",
        description: "Include any team projects or mentoring experience to demonstrate leadership and collaboration skills."
      }
    ]
  };

  // Mock progress data
  const progressData = {
    overall: 73,
    tracks: [
      {
        name: "AI/ML Track",
        icon: "Brain",
        color: "text-accent",
        progress: 75,
        completed: 9,
        total: 12,
        timeSpent: "156 hours"
      },
      {
        name: "Robotics Track",
        icon: "Bot",
        color: "text-cyber-purple",
        progress: 45,
        completed: 5,
        total: 11,
        timeSpent: "89 hours"
      },
      {
        name: "Coding Track",
        icon: "Code",
        color: "text-success",
        progress: 90,
        completed: 18,
        total: 20,
        timeSpent: "234 hours"
      }
    ],
    skillDevelopment: [
      {
        name: "Python Programming",
        level: 95,
        improvement: 8,
        color: "from-success to-success/80"
      },
      {
        name: "Machine Learning",
        level: 85,
        improvement: 12,
        color: "from-accent to-accent/80"
      },
      {
        name: "React Development",
        level: 80,
        improvement: 5,
        color: "from-cyber-purple to-cyber-purple/80"
      }
    ],
    milestones: [
      {
        title: "Complete AI Specialization",
        description: "Finish all courses in the AI/ML track",
        progress: 75,
        eta: "2 weeks",
        icon: "Brain",
        bgColor: "bg-accent",
        progressColor: "from-accent to-accent/80"
      },
      {
        title: "Build Capstone Project",
        description: "Create a comprehensive portfolio project",
        progress: 30,
        eta: "1 month",
        icon: "Rocket",
        bgColor: "bg-cyber-purple",
        progressColor: "from-cyber-purple to-cyber-purple/80"
      }
    ]
  };

  const streaksData = {
    current: 12,
    longest: 28,
    thisWeek: 5,
    weeklyActivity: [
      { day: "Mon", active: true },
      { day: "Tue", active: true },
      { day: "Wed", active: false },
      { day: "Thu", active: true },
      { day: "Fri", active: true },
      { day: "Sat", active: true },
      { day: "Sun", active: false }
    ]
  };

  const achievementsData = {
    recent: [
      {
        title: "Course Completed",
        description: "Finished Advanced Python Programming",
        date: "2 days ago",
        icon: "CheckCircle",
        bgColor: "bg-success"
      },
      {
        title: "Streak Milestone",
        description: "Maintained 10-day learning streak",
        date: "1 week ago",
        icon: "Flame",
        bgColor: "bg-warning"
      },
      {
        title: "Project Featured",
        description: "Your AI project was featured in showcase",
        date: "2 weeks ago",
        icon: "Star",
        bgColor: "bg-accent"
      }
    ]
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'courses', label: 'Courses', icon: 'BookOpen' },
    { id: 'mentorship', label: 'Mentorship', icon: 'Users' },
    { id: 'projects', label: 'Projects', icon: 'FolderOpen' },
    { id: 'competitions', label: 'Competitions', icon: 'Trophy' },
    { id: 'portfolio', label: 'Portfolio', icon: 'User' },
    { id: 'progress', label: 'Progress', icon: 'TrendingUp' }
  ];

  const courseFilters = [
    { id: 'all', label: 'All Courses' },
    { id: 'ai', label: 'AI/ML' },
    { id: 'robotics', label: 'Robotics' },
    { id: 'coding', label: 'Coding' },
    { id: 'in-progress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' }
  ];

  const handleCourseEnroll = (course) => {
    console.log('Enrolling in course:', course.title);
  };

  const handleMentorConnect = (mentor) => {
    console.log('Connecting with mentor:', mentor.name);
  };

  const handleProjectVote = (projectId) => {
    console.log('Voting for project:', projectId);
  };

  const handleProjectComment = (projectId) => {
    console.log('Commenting on project:', projectId);
  };

  const handleCompetitionJoin = (competitionId) => {
    console.log('Joining competition:', competitionId);
  };

  const handleAddProject = () => {
    console.log('Adding new project');
  };

  const handleEditProject = (projectId) => {
    console.log('Editing project:', projectId);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Individual Learning Portal - Dream Coders Club</title>
        <meta name="description" content="Your personalized learning dashboard for AI, Robotics, and Coding. Track progress, connect with mentors, and showcase your projects." />
        <meta name="keywords" content="learning portal, AI courses, robotics training, coding bootcamp, mentorship, portfolio builder" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="w-full px-4 lg:px-6 py-8">
            {/* Welcome Header */}
            <div className="mb-8">
              <WelcomeHeader user={userData} stats={userStats} />
            </div>

            {/* Navigation Tabs */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 bg-muted/20 p-2 rounded-lg">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'bg-accent text-white shadow-glow'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-8">
              {/* Dashboard */}
              {activeTab === 'dashboard' && (
                <div className="space-y-8">
                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Play"
                      iconPosition="left"
                      className="h-16 text-left justify-start"
                    >
                      <div>
                        <div className="font-medium">Continue Learning</div>
                        <div className="text-xs text-muted-foreground">Resume Python course</div>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="MessageCircle"
                      iconPosition="left"
                      className="h-16 text-left justify-start"
                    >
                      <div>
                        <div className="font-medium">Ask Mentor</div>
                        <div className="text-xs text-muted-foreground">Get help with projects</div>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Trophy"
                      iconPosition="left"
                      className="h-16 text-left justify-start"
                    >
                      <div>
                        <div className="font-medium">Join Competition</div>
                        <div className="text-xs text-muted-foreground">AI Hackathon active</div>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Share"
                      iconPosition="left"
                      className="h-16 text-left justify-start"
                    >
                      <div>
                        <div className="font-medium">Share Project</div>
                        <div className="text-xs text-muted-foreground">Show your work</div>
                      </div>
                    </Button>
                  </div>

                  {/* Recent Activity & Recommendations */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="holographic-card p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
                      <div className="space-y-4">
                        {[
                          { action: "Completed lesson", item: "Neural Networks Basics", time: "2 hours ago", icon: "CheckCircle", color: "text-success" },
                          { action: "Started course", item: "Advanced Robotics", time: "1 day ago", icon: "Play", color: "text-accent" },
                          { action: "Joined competition", item: "AI Hackathon 2024", time: "3 days ago", icon: "Trophy", color: "text-warning" }
                        ].map((activity, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-muted/20">
                            <Icon name={activity.icon} size={16} className={activity.color} />
                            <div className="flex-1">
                              <p className="text-sm text-foreground">{activity.action} <span className="font-medium">{activity.item}</span></p>
                              <p className="text-xs text-muted-foreground">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="holographic-card p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Recommended for You</h3>
                      <div className="space-y-4">
                        {coursesData.slice(0, 2).map((course) => (
                          <div key={course.id} className="flex items-center space-x-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors duration-300">
                            <div className="w-12 h-12 rounded-lg overflow-hidden">
                              <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-foreground text-sm">{course.title}</h4>
                              <p className="text-xs text-muted-foreground">{course.instructor.name} • {course.duration}</p>
                            </div>
                            <Button variant="ghost" size="sm" iconName="ChevronRight" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Courses */}
              {activeTab === 'courses' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground mb-2">Course Catalog</h2>
                      <p className="text-muted-foreground">Discover courses tailored to your learning goals</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <select
                        value={courseFilter}
                        onChange={(e) => setCourseFilter(e.target.value)}
                        className="px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
                      >
                        {courseFilters.map((filter) => (
                          <option key={filter.id} value={filter.id}>{filter.label}</option>
                        ))}
                      </select>
                      <Button variant="outline" iconName="Filter" iconPosition="left">
                        Filter
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {coursesData.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        onEnroll={handleCourseEnroll}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Mentorship */}
              {activeTab === 'mentorship' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground mb-2">Find Your Mentor</h2>
                      <p className="text-muted-foreground">Connect with industry experts and accelerate your learning</p>
                    </div>
                    <Button
                      variant="default"
                      iconName="Search"
                      iconPosition="left"
                      className="morphing-cta"
                    >
                      Browse All Mentors
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {mentorsData.map((mentor) => (
                      <MentorCard
                        key={mentor.id}
                        mentor={mentor}
                        onConnect={handleMentorConnect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {activeTab === 'projects' && (
                <ProjectShowcase
                  projects={projectsData}
                  onVote={handleProjectVote}
                  onComment={handleProjectComment}
                />
              )}

              {/* Competitions */}
              {activeTab === 'competitions' && (
                <CompetitionArena
                  competitions={competitionsData}
                  leaderboard={leaderboardData}
                  onJoin={handleCompetitionJoin}
                />
              )}

              {/* Portfolio */}
              {activeTab === 'portfolio' && (
                <PortfolioBuilder
                  portfolio={portfolioData}
                  onAddProject={handleAddProject}
                  onEditProject={handleEditProject}
                />
              )}

              {/* Progress */}
              {activeTab === 'progress' && (
                <ProgressTracker
                  progress={progressData}
                  achievements={achievementsData}
                  streaks={streaksData}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default IndividualLearningPortal;